/**
 * Headline gift-relevant US holidays for the calendar surface.
 *
 * Slice-1 design choice (per 2026-05-28 spec conversation): holidays are
 * FE constants, not BE-personalized. Every logged-in user with ≥1 board
 * sees the same set on the calendar.
 *
 * Tap destination — a holiday routes to at most ONE of:
 *   - `occasionSlug` → /occasion/{slug} (a marketing occasion page in
 *     OccasionRoute.tsx). Its `previewImages` are hardcoded here.
 *   - `guideSlug` → /guide/{slug} (a curated gift-guide page). Its preview
 *     art is NOT hardcoded — the calendar fills it at runtime from the
 *     materialized guide-previews doc keyed by this slug (see
 *     holidayEvents.ts + useGuidePreviewDoc). A holiday with neither slug
 *     is informational (no tap destination).
 * The two are mutually exclusive (enforced by holidays.test.ts).
 *
 * Gift Guide Hub cohort: when the hub gate is ENABLED and the hub's occasions
 * doc holds a collection keyed by the holiday's kebab-cased type
 * (`halloween`, `fourth-of-july`, …), the tap opens `/gift-guides/{key}`
 * instead — see `eventDestination` in mockCalendarData.ts. Nothing here
 * changes for that: preview art still comes from the slugs above.
 *
 * Adding a holiday-with-destination is a two-step change: add the page/route
 * (OccasionRoute.tsx for an occasion, or a /guide/{slug} route + listing for
 * a guide) and set the matching slug here. holidays.test.ts pins that every
 * `guideSlug` resolves to a real GUIDE_BROWSE_LISTINGS entry AND a live
 * /guide/{slug} route in App.js, so a renamed/removed guide breaks CI.
 *
 * Date computation:
 *   - Fixed-date holidays (Valentine's, Christmas, etc.) — exact day.
 *   - Floating holidays (Mother's Day = 2nd Sunday of May; Father's Day
 *     = 3rd Sunday of June; Thanksgiving = 4th Thursday of November;
 *     Teacher Appreciation Day = Tuesday of the first full week of May) —
 *     computed per-year via `nthWeekdayOfMonth` / `teacherAppreciationDay`.
 *   - Easter — the standard Gregorian computus algorithm (`easterDate`).
 *   - Grandparents Day — the first Sunday AFTER Labor Day, which is NOT a
 *     fixed nth-Sunday-of-September; see `grandparentsDay`.
 *   - Moon-based holidays (Lunar New Year, Eid al-Fitr, Eid al-Adha, Holi,
 *     Purim, Passover, Rosh Hashanah, Diwali, Hanukkah) — lunar/lunisolar
 *     calendars with no simple Gregorian rule, so each gets a precomputed
 *     2026–2035 table read through `fromTable`. Every table is registered in
 *     `MOON_TABLES`, and holidays.test.ts fails while there are fewer than
 *     two years of runway left on any of them — otherwise an exhausted table
 *     makes its holiday vanish from the calendar silently.
 *
 * The list is 26 holidays, fixed by the THE-746 spec (checked against the
 * "Thea Holidays" sheet) — not a list to extend or reorder by local
 * judgement. The 17 added in THE-748 are informational: no `occasionSlug`
 * and no `guideSlug`, so they render but do not tap through, until each one
 * gets a guide.
 */

export type HolidayEventType =
  | 'new_years'
  | 'mlk_day'
  | 'lunar_new_year'
  | 'valentines_day'
  | 'presidents_day'
  | 'womens_day'
  | 'eid_al_fitr'
  | 'st_patricks'
  | 'holi'
  | 'purim'
  | 'easter'
  | 'passover'
  | 'teacher_appreciation'
  | 'mothers_day'
  | 'eid_al_adha'
  | 'fathers_day'
  | 'fourth_of_july'
  | 'friendship_day'
  | 'grandparents_day'
  | 'rosh_hashanah'
  | 'halloween'
  | 'diwali'
  | 'thanksgiving'
  | 'hanukkah'
  | 'christmas'
  | 'kwanzaa';

/**
 * Membership table for `isHolidayEventType`. Typed as a total record over the
 * union rather than a string array so that adding a holiday to
 * `HolidayEventType` without adding it here is a TYPE error — otherwise the
 * new holiday would silently read as a person event and take the deep-clay
 * date pill plus the mark-done menu (THE-750).
 *
 * Deliberately NOT derived from `HOLIDAYS`: that list is the subset we
 * currently put on the calendar, and a holiday awaiting its entry is still a
 * holiday for colour and menu purposes.
 *
 * Kept in the SAME ORDER as the `HolidayEventType` union above so the two can
 * be diffed against each other by eye. The tripwire has already fired once:
 * THE-748 widened the union from 9 to 26 and this record went red, which is
 * the intended outcome — the 17 new holidays would otherwise have rendered as
 * person events with a deep-clay pill and a mark-done menu.
 */
const HOLIDAY_EVENT_TYPES: Record<HolidayEventType, true> = {
  new_years: true,
  mlk_day: true,
  lunar_new_year: true,
  valentines_day: true,
  presidents_day: true,
  womens_day: true,
  eid_al_fitr: true,
  st_patricks: true,
  holi: true,
  purim: true,
  easter: true,
  passover: true,
  teacher_appreciation: true,
  mothers_day: true,
  eid_al_adha: true,
  fathers_day: true,
  fourth_of_july: true,
  friendship_day: true,
  grandparents_day: true,
  rosh_hashanah: true,
  halloween: true,
  diwali: true,
  thanksgiving: true,
  hanukkah: true,
  christmas: true,
  kwanzaa: true,
};

/**
 * True for holiday event types, false for person types ('birthday',
 * 'anniversary'). Keyed on the event TYPE, not on whether a `recipient`
 * happens to be attached — a person event that arrives with a null recipient
 * is still one of her people.
 */
export function isHolidayEventType(eventType: string): eventType is HolidayEventType {
  return Object.prototype.hasOwnProperty.call(HOLIDAY_EVENT_TYPES, eventType);
}

/**
 * The glyph a holiday shows where a person shows their recipient emoji
 * (THE-793 item 2 — Halloween was rendering with no emoji at all).
 *
 * A total `Record<HolidayEventType, string>` for the same reason
 * `HOLIDAY_EVENT_TYPES` above is one: adding a holiday to the union without
 * giving it a glyph is a TYPE error, not a holiday that silently renders
 * blank. Kept in union order so the two tables diff by eye.
 *
 * One glyph per holiday, and deliberately the OBJECT most associated with
 * the day rather than a gift/present symbol — a 🎁 on Halloween was exactly
 * the "wrong emoji" half of the QA note. The civic days that have no single
 * object of their own (MLK, Presidents', Women's, Friendship, Grandparents)
 * take the closest unambiguous symbol instead of being left empty; a blank
 * slot collapses the card's top row and is the bug this table fixes.
 */
const HOLIDAY_EMOJI: Record<HolidayEventType, string> = {
  new_years: '🎉',
  mlk_day: '🕊️',
  lunar_new_year: '🧧',
  valentines_day: '💝',
  presidents_day: '🇺🇸',
  womens_day: '♀️',
  eid_al_fitr: '🌙',
  st_patricks: '🍀',
  holi: '🎨',
  purim: '📜',
  easter: '🐣',
  passover: '🍷',
  teacher_appreciation: '🍎',
  mothers_day: '💐',
  eid_al_adha: '🕌',
  fathers_day: '👔',
  fourth_of_july: '🎆',
  friendship_day: '🤝',
  grandparents_day: '👵',
  rosh_hashanah: '🍯',
  halloween: '🎃',
  diwali: '🪔',
  thanksgiving: '🦃',
  hanukkah: '🕎',
  christmas: '🎄',
  kwanzaa: '🕯️',
};

/**
 * The display glyph for a holiday event type.
 *
 * Returns `undefined` — never a placeholder — for anything that is not a
 * holiday, so a caller cannot accidentally paint a holiday glyph on a person
 * card. Call sites render the emoji slot only when this returns a string.
 */
export function holidayEmoji(eventType: string): string | undefined {
  return isHolidayEventType(eventType) ? HOLIDAY_EMOJI[eventType] : undefined;
}

export interface HolidayEntry {
  eventType: HolidayEventType;
  label: string;
  /** If set, the card body taps route to /occasion/{slug}. Mutually
   *  exclusive with `guideSlug`. If both are undefined, the card has no
   *  tap destination (informational). */
  occasionSlug?: string;
  /** If set, the card body taps route to /guide/{slug} (a curated gift
   *  guide). Unlike `occasionSlug`, the preview art is NOT hardcoded on
   *  this entry: the calendar resolves it at runtime from the materialized
   *  guide-previews doc keyed by this slug (holidayEvents.ts). Keep
   *  `productThumbs` set as the degraded fallback for the pre-load / doc-
   *  absent / malformed-strip window. Mutually exclusive with
   *  `occasionSlug`. */
  guideSlug?: string;
  /** Per-year date computation. Returns YYYY-MM-DD, or null when the
   *  date isn't computable for that year (Hanukkah past its table). */
  dateFor: (year: number) => string | null;
  /** Thematic color thumbs for holidays without curated product images
   *  (Halloween, Thanksgiving, etc.). Soft palette tied to the holiday's
   *  vibe — used only when previewImages is missing. */
  productThumbs?: string[];
  /** Up to 3 curated product image URLs. When set, the calendar event
   *  card renders the carousel-style preview (center + 2 peeks) instead
   *  of themed thumbs. Pulled from the same `/occasion/{slug}` sample
   *  data (top-of-page products) so the calendar preview reflects what
   *  the user lands on when they tap the card. */
  previewImages?: string[];
}

// Common helper: compute the nth occurrence of a weekday in a month.
// month: 1-indexed (1 = January … 12 = December) — the SAME convention as
// iso(), on purpose. This file once mixed 0- and 1-indexed months and the
// mix-up shipped Christmas on Nov 25 (#327); every month in this file is
// 1-indexed now.
// weekday: 0 = Sunday, 1 = Monday, …, 4 = Thursday, etc.
// nth: 1-indexed (1 = first occurrence, 2 = second, …).
function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): string {
  const first = new Date(year, month - 1, 1);
  const firstWeekday = first.getDay();
  const offset = (weekday - firstWeekday + 7) % 7;
  const day = 1 + offset + (nth - 1) * 7;
  return iso(year, month, day);
}

function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// National Teacher Appreciation Day: the Tuesday of the first FULL
// (Mon–Fri) week of May. That Tuesday is the day after May's first
// Monday, so: first Monday + 1 day. When May 1st is a Monday the first
// full week starts May 1 and Teacher Day is May 2; when May 1st is a
// Tuesday the first full Mon–Fri week starts May 7 — first-Monday+1
// handles both, unlike a naive "first Tuesday of May".
function teacherAppreciationDay(year: number): string {
  const firstMonday = nthWeekdayOfMonth(year, 5 /* May */, 1 /* Monday */, 1);
  const day = parseInt(firstMonday.slice(8, 10), 10) + 1;
  return iso(year, 5, day);
}

// National Grandparents Day: the first SUNDAY AFTER Labor Day, where Labor
// Day is the first Monday of September (36 U.S.C. §138).
//
// This CANNOT be expressed as `nthWeekdayOfMonth(year, 9, 0, n)` for a fixed
// n, which is the trap worth spelling out: it is September's 2nd Sunday in
// most years, but its 1st Sunday whenever Sept 1 is a Monday. Then Labor Day
// is Sep 1, the first Sunday after it is Sep 7 — September's FIRST Sunday —
// while the 2nd Sunday is Sep 14, a week late. In 2026–2036 that happens in
// 2031 and 2036 (see holidays.test.ts, which pins both branches).
//
// Expressed off Labor Day directly, it is just "Labor Day + 6 days": the
// Monday plus six days is always the Sunday that follows it, and since Labor
// Day is at the latest Sep 7, the result is at the latest Sep 13 — always
// inside September, so no month rollover to handle.
function grandparentsDay(year: number): string {
  const laborDay = nthWeekdayOfMonth(year, 9 /* September */, 1 /* Monday */, 1);
  const day = parseInt(laborDay.slice(8, 10), 10) + 6;
  return iso(year, 9, day);
}

// Easter Sunday via the anonymous Gregorian computus (Meeus/Jones/Butcher).
// Deterministic arithmetic — no tables, valid for all Gregorian years.
function easterDate(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(year, month, day);
}

// Hanukkah rides the Hebrew calendar (starts 25 Kislev), which has no
// simple Gregorian formula — so: a precomputed table of FIRST NIGHTS
// (the evening candles are first lit and gifts start, i.e. the day
// before the first full day). Generated from the Hebrew-calendar
// conversion in `convertdate` (25 Kislev, minus one day).
// NOTE: extend this table before 2035 or Hanukkah silently drops off
// the calendar — `hanukkahFirstNight` returns null past the last entry
// and the holidaysInRange walk skips it.
const HANUKKAH_FIRST_NIGHT: Record<number, string> = {
  2026: '2026-12-04',
  2027: '2027-12-24',
  2028: '2028-12-12',
  2029: '2029-12-01',
  2030: '2030-12-20',
  2031: '2031-12-09',
  2032: '2032-11-27',
  2033: '2033-12-16',
  2034: '2034-12-06',
  2035: '2035-12-25',
};

// ---------------------------------------------------------------------------
// Moon-based holidays: precomputed 2026–2035 tables.
//
// Every table below follows the HANUKKAH_FIRST_NIGHT shape — Gregorian year →
// YYYY-MM-DD — and is looked up through `fromTable`, so an exhausted table
// returns null and the holiday is skipped rather than guessed at.
//
// These are religious and cultural dates: a wrong one is worse than a missing
// one. Each table names the published source it was checked against, and the
// per-table source attribution is repeated in the PR body. Where a table was
// ALSO reproduced independently (a calendar library, or a moon-phase
// computation) that second check is noted, because agreement between two
// independent derivations is the only thing that catches a transcription slip.
//
// A note on what these dates are NOT: a single Gregorian date per year is a
// lossy model of a lunar observance. Two consequences are called out in the
// tables below — the Islamic tables lose an occurrence in 2033 (EID_AL_FITR),
// and several of these holidays can shift a day by locale or moon sighting.
// MOON_TABLES at the bottom exists so the exhaustion guard in holidays.test.ts
// covers all of them generically, including any table added later.
// ---------------------------------------------------------------------------

// Lunar New Year (first day of the first month of the Chinese lunisolar
// calendar — the new moon between Jan 21 and Feb 20).
// Source: Wikipedia "Chinese New Year" table of dates, cross-checked against
// travelchinaguide.com's published 2026–2035 list. Both agree on all 10 years.
const LUNAR_NEW_YEAR: Record<number, string> = {
  2026: '2026-02-17',
  2027: '2027-02-06',
  2028: '2028-01-26',
  2029: '2029-02-13',
  2030: '2030-02-03',
  2031: '2031-01-23',
  2032: '2032-02-11',
  2033: '2033-01-31',
  2034: '2034-02-19',
  2035: '2035-02-08',
};

// Eid al-Fitr (1 Shawwal) — the "(approx.)" in the label is load-bearing:
// local moon sighting moves this a day either way in many communities.
// Source: Umm al-Qura calendar (the Saudi civil calendar) as published by
// qppstudio.net's 10-year Eid al-Fitr table, independently reproduced by
// converting 1 Shawwal AH 1447–1457 through the aladhan.com Hijri API. Both
// agree on all 10 years.
//
// ⚠️ KNOWN LOSS — 2033 has TWO Eid al-Fitrs (1 Shawwal 1454 = 2033-01-02 and
// 1 Shawwal 1455 = 2033-12-23), because the Hijri year is ~11 days shorter
// than the Gregorian one. A `Record<year, string>` holds one, and `dateFor`
// returns one date per year, so the SECOND one is unreachable: the calendar
// will not show an Eid al-Fitr card for 2033-12-23. The table keeps the
// chronologically first Eid of each Gregorian year. holidays.test.ts pins
// this omission so it is documented and loud rather than a silent gap; fixing
// it needs a `dateFor` that can return more than one date, which is a change
// to the rendering contract and is deliberately not in this PR.
const EID_AL_FITR: Record<number, string> = {
  2026: '2026-03-20',
  2027: '2027-03-09',
  2028: '2028-02-26',
  2029: '2029-02-14',
  2030: '2030-02-04',
  2031: '2031-01-24',
  2032: '2032-01-14',
  2033: '2033-01-02', // + 2033-12-23 is unreachable; see note above.
  2034: '2034-12-12',
  2035: '2035-12-01',
};

/** The Eid al-Fitr occurrences the year-keyed table above cannot represent.
 *  Exported so holidays.test.ts can assert the gap stays documented instead
 *  of silently widening if the table is ever regenerated. */
export const EID_AL_FITR_UNREPRESENTABLE: readonly string[] = ['2033-12-23'];

// Eid al-Adha (10 Dhu al-Hijjah). Same "(approx.)" caveat as Eid al-Fitr.
// Source: Umm al-Qura via qppstudio.net's 10-year Eid al-Adha table,
// independently reproduced through the aladhan.com Hijri API (10 Dhu
// al-Hijjah AH 1447–1456). Both agree on all 10 years. Unlike Eid al-Fitr,
// Eid al-Adha falls exactly once in each Gregorian year 2026–2035.
const EID_AL_ADHA: Record<number, string> = {
  2026: '2026-05-27',
  2027: '2027-05-16',
  2028: '2028-05-05',
  2029: '2029-04-24',
  2030: '2030-04-13',
  2031: '2031-04-02',
  2032: '2032-03-22',
  2033: '2033-03-11',
  2034: '2034-03-01',
  2035: '2035-02-18',
};

// Holi — the RANGWALI HOLI / Dhulandi day, when colours are played. That is
// the day people mean by "Holi", and it is the day AFTER Holika Dahan (the
// bonfire, on Phalguna Purnima). Picking the wrong one of the two is the easy
// mistake here: general-purpose holiday aggregators list the two
// interchangeably, so several of them disagree with this table by a day.
// Source: Drik Panchang's Rangwali Holi date, New Delhi locale, read per year
// for 2026–2035. Cross-checked against an independent Meeus moon-phase
// computation of Phalguna Purnima: the computed full-moon instants match Drik
// Panchang's published Purnima end times to within ~2 minutes, and the
// derived Rangwali date agrees for 9 of 10 years. The exception is 2026,
// where a total lunar eclipse on Mar 3 defers Holika Dahan in North India to
// Mar 3 and Rangwali Holi to Mar 4 — the published date is authoritative over
// the arithmetic there, which is exactly why this is a table and not a
// formula. Note these dates can differ by a day for other locales.
const HOLI: Record<number, string> = {
  2026: '2026-03-04',
  2027: '2027-03-22',
  2028: '2028-03-11',
  2029: '2029-03-01',
  2030: '2030-03-20',
  2031: '2031-03-09',
  2032: '2032-03-27',
  2033: '2033-03-16',
  2034: '2034-03-05',
  2035: '2035-03-24',
};

// Purim (14 Adar; 14 Adar II in a Hebrew leap year — using plain Adar in a
// leap year would land on Purim Katan, a month early).
// Source: Hebcal's published holiday calendar for each year 2026–2035,
// independently reproduced with the Hebrew-calendar conversion in
// `convertdate` (14 Adar/Veadar). Both agree on all 10 years.
const PURIM: Record<number, string> = {
  2026: '2026-03-03',
  2027: '2027-03-23',
  2028: '2028-03-12',
  2029: '2029-03-01',
  2030: '2030-03-19',
  2031: '2031-03-09',
  2032: '2032-02-26',
  2033: '2033-03-15',
  2034: '2034-03-05',
  2035: '2035-03-25',
};

// Passover — first day of Pesach (15 Nisan), which is Hebcal's "Pesach I".
// NOTE the convention difference from Hanukkah below it: HANUKKAH_FIRST_NIGHT
// anchors on the EVENING BEFORE (24 Kislev) because that is when candles and
// gifts start, whereas this table anchors on the holiday's first DAY. The
// first seder is the preceding evening (Erev Pesach, 15 Nisan minus one day),
// so a card dated from this table reads one day later than the seder night.
// Source: Hebcal "Pesach I" for each year 2026–2035, independently reproduced
// with `convertdate` (15 Nisan). Both agree on all 10 years; Hebcal's "Erev
// Pesach" is each of these minus one day, if the anchor is ever revisited.
const PASSOVER: Record<number, string> = {
  2026: '2026-04-02',
  2027: '2027-04-22',
  2028: '2028-04-11',
  2029: '2029-03-31',
  2030: '2030-04-18',
  2031: '2031-04-08',
  2032: '2032-03-27',
  2033: '2033-04-14',
  2034: '2034-04-04',
  2035: '2035-04-24',
};

// Rosh Hashanah — first day (1 Tishrei). Same first-DAY anchor as PASSOVER
// above; the erev meal is the preceding evening.
// Source: Hebcal for each year 2026–2035, independently reproduced with
// `convertdate` (1 Tishrei). Both agree on all 10 years.
const ROSH_HASHANAH: Record<number, string> = {
  2026: '2026-09-12',
  2027: '2027-10-02',
  2028: '2028-09-21',
  2029: '2029-09-10',
  2030: '2030-09-28',
  2031: '2031-09-18',
  2032: '2032-09-06',
  2033: '2033-09-24',
  2034: '2034-09-14',
  2035: '2035-10-04',
};

// Diwali — the main day, Lakshmi Puja, on Kartika Amavasya (new moon).
// Source: the published 2026–2035 Diwali list carried by Wikipedia's Diwali
// article and publicholidays.com, spot-checked against Drik Panchang for
// 2026 (Lakshmi Puja Sun 8 Nov 2026). Independently reproduced with a Meeus
// new-moon computation under the standard rule that Lakshmi Puja falls on the
// day whose pradosh-kaal (evening) carries Amavasya tithi — i.e. the day
// before the new-moon instant unless that instant falls after dusk IST. That
// derivation agrees with the published list on all 10 years, including 2027,
// the only year here where the new moon is late enough (19:07 IST) to keep
// Diwali on the same date as the instant.
const DIWALI: Record<number, string> = {
  2026: '2026-11-08',
  2027: '2027-10-29',
  2028: '2028-10-17',
  2029: '2029-11-05',
  2030: '2030-10-26',
  2031: '2031-11-14',
  2032: '2032-11-02',
  2033: '2033-10-22',
  2034: '2034-11-10',
  2035: '2035-10-30',
};

/** Table lookup for a moon-based holiday: the date for `year`, or null once
 *  the table runs out. Null makes the holiday skip (holidaysInRange drops
 *  it) rather than render a guessed date. */
function fromTable(table: Record<number, string>): (year: number) => string | null {
  return (year) => table[year] ?? null;
}

/**
 * Every precomputed moon table, by the eventType that reads it.
 *
 * This registry exists for the exhaustion guard in holidays.test.ts: these
 * tables END, and when one does, `dateFor` returns null and the holiday
 * silently disappears from the calendar with nothing warning that it is
 * about to. The test walks THIS map rather than a hardcoded list, so adding
 * a tenth table gets the guard for free — the only thing a new table has to
 * do is appear here.
 */
export const MOON_TABLES: Readonly<Record<string, Record<number, string>>> = {
  lunar_new_year: LUNAR_NEW_YEAR,
  eid_al_fitr: EID_AL_FITR,
  holi: HOLI,
  purim: PURIM,
  passover: PASSOVER,
  eid_al_adha: EID_AL_ADHA,
  rosh_hashanah: ROSH_HASHANAH,
  diwali: DIWALI,
  hanukkah: HANUKKAH_FIRST_NIGHT,
};

function hanukkahFirstNight(year: number): string | null {
  return HANUKKAH_FIRST_NIGHT[year] ?? null;
}

// Soft, on-brand thumb palettes per holiday. 3 colors each — the card
// renders three overlapping circular thumbs from this list. Color choices
// loosely match each occasion's emotional palette (pinks for Valentine's
// + Mother's, sage/clay for Father's, etc.).
const PINK = '#F4C4C9';
const SAGE = '#C7DBC1';
const CLAY = '#D9B19E';
const CREAM_GOLD = '#E8D9A8';
const TAN = '#D8B68F';
const ROSE = '#E5BFC5';
const ORANGE = '#E8B690';
const FOREST = '#A3BFA0';
const DEEP_RED = '#C97A6E';
const SOFT_BLUE = '#A9C2D9';
const CREAM = '#F1E9DA';
// Added for the 17 holidays in THE-748, in the same soft, desaturated register
// as the originals above (nothing fully saturated, nothing grey). Reuse the
// constants above wherever they already fit — these only cover gaps.
const CHAMPAGNE = '#EADFC8'; // New Year's / Friendship — pale sparkle
const LANTERN_RED = '#D98C84'; // Lunar New Year — soft lantern red
const DUSK_BLUE = '#8FA8C4'; // MLK / Presidents' — deeper civic blue
const PLUM = '#C2A3BE'; // Women's Day / Purim — muted plum
const MINT = '#BCD6C8'; // Eid — soft mint-green
const MARIGOLD = '#E9C27E'; // Holi / Diwali — marigold yellow
const SHAMROCK = '#9FC49A'; // St. Patrick's — soft shamrock
const LILAC = '#C9BEDC'; // Holi — powder lilac
const HONEY = '#E3C68C'; // Passover / Rosh Hashanah — honey
const SLATE_BLUE = '#9DB0C9'; // Grandparents — gentle slate
const POMEGRANATE = '#C98A94'; // Rosh Hashanah — pomegranate
const KENTE_GREEN = '#8FB08A'; // Kwanzaa — deeper green of the kente triad
const KENTE_GOLD = '#E0B878'; // Kwanzaa — warm gold

// The 17 holidays added in THE-748 all carry NEITHER `occasionSlug` NOR
// `guideSlug`: none of them has an occasion page or a guide yet, so they are
// informational (no tap destination) until one is built. Each still gets a
// themed productThumbs triple — per the mockups, a new holiday shows its own
// palette, never a grey placeholder.
export const HOLIDAYS: HolidayEntry[] = [
  {
    eventType: 'new_years',
    label: "New Year's Day",
    dateFor: (year) => iso(year, 1, 1),
    productThumbs: [CHAMPAGNE, CREAM_GOLD, SOFT_BLUE],
  },
  {
    eventType: 'mlk_day',
    label: 'MLK Day',
    // 3rd Monday in January.
    dateFor: (year) => nthWeekdayOfMonth(year, 1 /* January */, 1 /* Monday */, 3),
    productThumbs: [DUSK_BLUE, CREAM, TAN],
  },
  {
    eventType: 'lunar_new_year',
    label: 'Lunar New Year',
    // Table-driven — see LUNAR_NEW_YEAR.
    dateFor: fromTable(LUNAR_NEW_YEAR),
    productThumbs: [LANTERN_RED, CREAM_GOLD, DEEP_RED],
  },
  {
    eventType: 'valentines_day',
    label: "Valentine's Day",
    // No occasion route yet — informational only.
    dateFor: (year) => iso(year, 2, 14),
    productThumbs: [PINK, ROSE, DEEP_RED],
  },
  {
    eventType: 'presidents_day',
    label: "Presidents' Day",
    // 3rd Monday in February.
    dateFor: (year) => nthWeekdayOfMonth(year, 2 /* February */, 1 /* Monday */, 3),
    productThumbs: [DUSK_BLUE, CREAM, DEEP_RED],
  },
  {
    eventType: 'womens_day',
    label: "Women's Day",
    dateFor: (year) => iso(year, 3, 8),
    productThumbs: [PLUM, PINK, CREAM],
  },
  {
    eventType: 'eid_al_fitr',
    // "(approx.)" is part of the name on purpose: moon sighting can shift the
    // observed date by a day and a holiday card has no second line to say so.
    label: 'Eid al-Fitr (approx.)',
    // Table-driven — see EID_AL_FITR (and its 2033 double-Eid note).
    dateFor: fromTable(EID_AL_FITR),
    productThumbs: [MINT, CREAM, CREAM_GOLD],
  },
  {
    eventType: 'st_patricks',
    label: "St. Patrick's Day",
    dateFor: (year) => iso(year, 3, 17),
    productThumbs: [SHAMROCK, FOREST, CREAM_GOLD],
  },
  {
    eventType: 'holi',
    label: 'Holi',
    // Table-driven — see HOLI (Rangwali Holi, the colours day).
    dateFor: fromTable(HOLI),
    productThumbs: [MARIGOLD, LILAC, PINK],
  },
  {
    eventType: 'purim',
    label: 'Purim',
    // Table-driven — see PURIM.
    dateFor: fromTable(PURIM),
    productThumbs: [PLUM, CREAM_GOLD, SOFT_BLUE],
  },
  {
    eventType: 'easter',
    label: 'Easter',
    // No occasion route yet — informational only.
    dateFor: easterDate,
    productThumbs: [PINK, SAGE, CREAM_GOLD],
  },
  {
    eventType: 'passover',
    label: 'Passover',
    // Table-driven — see PASSOVER (first day, 15 Nisan; the seder is the
    // evening before).
    dateFor: fromTable(PASSOVER),
    productThumbs: [HONEY, SOFT_BLUE, CREAM],
  },
  {
    eventType: 'teacher_appreciation',
    label: 'Teacher Appreciation Day',
    // /occasion/teacher_appreciation already exists (TeacherAppreciationPage).
    occasionSlug: 'teacher_appreciation',
    // Tuesday of the first full week of May (National Teacher Day).
    dateFor: teacherAppreciationDay,
    productThumbs: [DEEP_RED, SAGE, CREAM],
    // Top-of-page products on /occasion/teacher_appreciation (from
    // sampleTeacherAppreciationProducts.ts — retailer image URLs, same
    // ones the page itself renders). Center first, then left + right peeks.
    previewImages: [
      'https://www.bando.com/cdn/shop/files/bando-3p-chronicle-andy-warhol-soup-can-crayons-sharpener-02.jpg?v=1769098451',
      'https://m.media-amazon.com/images/I/61p8ntkT6EL._SL1417_.jpg',
      'https://i.etsystatic.com/32887498/r/il/75caa0/6864715039/il_1588xN.6864715039_dlwj.jpg',
    ],
  },
  {
    eventType: 'mothers_day',
    label: "Mother's Day",
    occasionSlug: 'mothers_day',
    // 2nd Sunday in May.
    dateFor: (year) => nthWeekdayOfMonth(year, 5 /* May */, 0 /* Sunday */, 2),
    productThumbs: [PINK, ROSE, CREAM_GOLD],
    // Top-of-page products on /occasion/mothers_day (from
    // sampleMothersDayCarousels.ts). Center first, then left + right peeks.
    previewImages: [
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2F_carousel%2F67d38d535356a3f0_orig.webp?alt=media&token=8a6c7a9e-c16c-4856-92eb-d4841cbcae62',
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2Fd5iqmjRUnHj7ghRlwzqX%2F5c5c15ffbcafb784_orig.webp?alt=media&token=2fe38b28-fb04-4f6c-9279-e8df30b19f99',
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2FGoscVm9rCtaUH8nOcQx9%2Fde9e33397bf33f6d_orig.webp?alt=media&token=026f1618-d1a9-4074-ab76-17d9e0088cc9',
    ],
  },
  {
    eventType: 'eid_al_adha',
    // "(approx.)" is part of the name — same moon-sighting caveat as al-Fitr.
    label: 'Eid al-Adha (approx.)',
    // Table-driven — see EID_AL_ADHA.
    dateFor: fromTable(EID_AL_ADHA),
    productThumbs: [MINT, HONEY, CREAM],
  },
  {
    eventType: 'fathers_day',
    label: "Father's Day",
    occasionSlug: 'fathers_day',
    // 3rd Sunday in June.
    dateFor: (year) => nthWeekdayOfMonth(year, 6 /* June */, 0 /* Sunday */, 3),
    productThumbs: [SAGE, TAN, CLAY],
    // Top-of-page products on /occasion/fathers_day (from
    // sampleFathersDayCarousels.ts). Center first, then left + right peeks.
    previewImages: [
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2F2936FWLldWFUBbZ6bgN6%2F46949e4c25b228b1_orig.webp?alt=media&token=c8df3726-7c33-4634-8c7f-7586d205243f',
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2FfwJrTDUsO9ldeeFvfEfK%2Fb6e7f501e656cf24_orig.webp?alt=media&token=dba1270c-3e7a-4010-bd1c-9c896984b68e',
      'https://firebasestorage.googleapis.com/v0/b/thea-643b1.firebasestorage.app/o/products%2F_carousel%2Fbe9e70ebd7c31b59_orig.webp?alt=media&token=6cc451ed-2cb1-464d-8095-8a159b15930a',
    ],
  },
  {
    eventType: 'fourth_of_july',
    label: '4th of July',
    dateFor: (year) => iso(year, 7, 4),
    productThumbs: [SOFT_BLUE, DEEP_RED, CREAM],
  },
  {
    eventType: 'friendship_day',
    label: 'Friendship Day',
    // 1st Sunday in August (US National Friendship Day).
    dateFor: (year) => nthWeekdayOfMonth(year, 8 /* August */, 0 /* Sunday */, 1),
    productThumbs: [CHAMPAGNE, SAGE, ROSE],
  },
  {
    eventType: 'grandparents_day',
    label: 'Grandparents Day',
    // First Sunday AFTER Labor Day — NOT a fixed nth Sunday of September.
    // See grandparentsDay() for why nthWeekdayOfMonth can't express it.
    dateFor: grandparentsDay,
    productThumbs: [SLATE_BLUE, CREAM, TAN],
  },
  {
    eventType: 'rosh_hashanah',
    label: 'Rosh Hashanah',
    // Taps to the Rosh Hashanah gift guide (Firestore-native, published
    // 2026-10-03 — not in the legacy registry, so the calendar's
    // materialized-previews lookup has no art for it yet and the card renders
    // the productThumbs colour tiles below, tappable).
    guideSlug: 'rosh-hashanah',
    // Table-driven — see ROSH_HASHANAH (first day, 1 Tishrei).
    dateFor: fromTable(ROSH_HASHANAH),
    productThumbs: [HONEY, POMEGRANATE, CREAM_GOLD],
  },
  {
    eventType: 'halloween',
    label: 'Halloween',
    // Taps to the Halloween hosting guide; preview art resolves at runtime
    // from the guide-previews doc keyed by this slug. productThumbs is the
    // degraded fallback (doc not yet loaded / absent / malformed strip).
    guideSlug: 'halloween-hosting',
    dateFor: (year) => iso(year, 10, 31),
    productThumbs: [ORANGE, CLAY, TAN],
  },
  {
    eventType: 'diwali',
    label: 'Diwali',
    // Taps to the Diwali gift guide (Firestore-native, published 2026-10-03 —
    // not in the legacy registry, so the calendar's materialized-previews
    // lookup has no art for it yet and the card renders the productThumbs
    // colour tiles below, tappable).
    guideSlug: 'diwali',
    // Table-driven — see DIWALI (main day / Lakshmi Puja).
    dateFor: fromTable(DIWALI),
    productThumbs: [MARIGOLD, DEEP_RED, CREAM_GOLD],
  },
  {
    eventType: 'thanksgiving',
    label: 'Thanksgiving',
    // Taps to the Thanksgiving hosting guide; preview art resolves at
    // runtime from the guide-previews doc keyed by this slug. productThumbs
    // is the degraded fallback.
    guideSlug: 'thanksgiving-hosting',
    // 4th Thursday in November.
    dateFor: (year) => nthWeekdayOfMonth(year, 11 /* November */, 4 /* Thursday */, 4),
    productThumbs: [ORANGE, CREAM_GOLD, FOREST],
  },
  {
    eventType: 'hanukkah',
    label: 'Hanukkah',
    // Taps to the Little latkes (Hanukkah kids) guide — Firestore-native,
    // published 2026-10-03; preview art falls back to productThumbs below
    // until the calendar reads inline index art.
    guideSlug: 'little-latkes',
    // First night (candles + gifts). Table-driven — see HANUKKAH_FIRST_NIGHT.
    dateFor: hanukkahFirstNight,
    productThumbs: [SOFT_BLUE, CREAM, CREAM_GOLD],
  },
  {
    eventType: 'christmas',
    label: 'Christmas',
    dateFor: (year) => iso(year, 12, 25),
    productThumbs: [FOREST, DEEP_RED, CREAM_GOLD],
  },
  {
    eventType: 'kwanzaa',
    label: 'Kwanzaa',
    // First day of Kwanzaa (it runs Dec 26 – Jan 1).
    dateFor: (year) => iso(year, 12, 26),
    productThumbs: [KENTE_GREEN, DEEP_RED, KENTE_GOLD],
  },
];

/**
 * Return the set of holiday entries whose date falls between `from` and
 * `to` (inclusive). Walks every year touched by the range so longer
 * windows retain their final year's holidays.
 */
export function holidaysInRange(
  from: Date,
  to: Date,
): Array<HolidayEntry & { date: string }> {
  const fromIso = iso(from.getFullYear(), from.getMonth() + 1, from.getDate());
  const toIso = iso(to.getFullYear(), to.getMonth() + 1, to.getDate());
  const out: Array<HolidayEntry & { date: string }> = [];
  for (let year = from.getFullYear(); year <= to.getFullYear(); year += 1) {
    for (const h of HOLIDAYS) {
      const date = h.dateFor(year);
      if (date !== null && date >= fromIso && date <= toIso) {
        out.push({ ...h, date });
      }
    }
  }
  return out;
}
